import { HashSet } from "../Utils/HashSet";
import { MacAddress } from "./MacAddress";
import { Dictionary } from "../Utils/Dictionary";
import { VirtualHostInfo } from "../InfoSets/VirtualHostInfo";
import { ObjectSerializer } from "../Serialization/ObjectSerializer";
import { ObjSerUtils } from "../Serialization/ObjSerUtils";
import { ValueEncoder } from "../Utils/ValueEncoder";

export class VirtualPort implements ObjectSerializer {

	public Key: string;
	public get HasKey(): boolean {
		return !!this.Key;
	}

	public PortType: string;
	public get HasPortType(): boolean {
		return !!this.PortType;
	}

	public MacAddresses: HashSet<MacAddress>;
	public get HasMacAddress(): boolean {
		return this.MacAddresses && this.MacAddresses.Count > 0;
	}

	constructor(key?: string) {
		this.Key = key;
		this.Init();
	}

	private Init(): void {
		this.PortType = '';
		this.MacAddresses = new HashSet<MacAddress>();
	}

	public Write(obj: any, context: any): void {
		obj.virtual_port_entry = {};
		obj = obj.virtual_port_entry;

		obj.key = this.Key;

		if (this.HasPortType)
			obj.port_type = this.PortType;

		if (this.MacAddresses != null && this.MacAddresses.Count > 0) {
			obj.mac_address_set = [];
			ObjSerUtils.WriteCollection(obj.mac_address_set, this.MacAddresses, (value: MacAddress) => {
				return { list_value: ValueEncoder.EncodeMacAddress(value) };
			}, context);
		}
	}

	public Read(obj: any, context: any): void {
		if (!obj.virtual_port_entry)
			throw new Error();
		obj = obj.virtual_port_entry;

		if (obj.key !== undefined)
			this.Key = obj.key;

		if (obj.port_type !== undefined)
			this.PortType = obj.port_type;

		if (obj.mac_address_set !== undefined) {
			ObjSerUtils.ReadCollection(obj.mac_address_set, this.MacAddresses, (elem: any) => {
				return ValueEncoder.DecodeMacAddress(elem.list_value);
			}, context);
		}
	}
}

export class VirtualPortTable implements ObjectSerializer {

	private _portTable: Dictionary<string, VirtualPort>;

	constructor()
	{
		this._portTable = new Dictionary<string, VirtualPort>();
		return;
	}

	public Add(portKey: string): VirtualPort {
		var vPort: VirtualPort = null;

		if (this._portTable.ContainsKey(portKey)) {
			vPort = this._portTable.Item(portKey);
		}
		else {
			vPort = new VirtualPort(portKey);
			this._portTable.Add(portKey, vPort);
		}

		return vPort;
	}

	public AddPort(vPort: VirtualPort):void {
		if (!vPort.HasKey)
			return;

		if (this._portTable.ContainsKey(vPort.Key))
			this._portTable.Remove(vPort.Key);

		this._portTable.Add(vPort.Key, vPort);
	}

	public Clear():void {
		this._portTable.Clear();
	}

	public get Count():number {
		return this._portTable.Count;
	}

	public Remove(portKey: string):boolean {
		return this._portTable.Remove(portKey);
	}

	public Write(obj: any, context: any): void {
		obj.virtual_port_table = {};
		obj = obj.virtual_port_table;

		obj.entries = [];
		ObjSerUtils.WriteObjSerCollection(obj.entries, this._portTable.Values(), context);
	}

	public Read(obj: any, context: any): void {
		if (!obj.virtual_port_table)
			throw new Error();
		obj = obj.virtual_port_table;

		this.Clear();

		if (obj.entries !== undefined) {
			obj.entries.forEach((value: any) => {
				let entry = new VirtualPort();
				entry.Read(value, context);
				this.AddPort(entry);
			}, this);
		}
	}
}

export class VirtualPortGroup implements ObjectSerializer {

	public Key: string;
	public get HasKey(): boolean {
		return !!this.Key;
	}

	public Name: string;
	public get HasName(): boolean {
		return !!this.Name;
	}

	public HostID: string;
	public get HasHostID(): boolean {
		return !!this.HostID;
	}

	public VirtualSwitchKey: string;
	public get HasVirtualSwitchKey(): boolean {
		return !!this.VirtualSwitchKey;
	}

	public VirtualSwitch: string;
	public get HasVirtualSwitch(): boolean {
		return !!this.VirtualSwitch;
	}

	public NetworkName: string;
	public get HasNetworkName(): boolean {
		return !!this.NetworkName;
	}

	public VlanId: number;
	public get HasVlanId(): boolean {
		return this.VlanId > 0;
	}

	public Ports: VirtualPortTable;

	constructor(portGroupKey?: string, hostID?:string){
		this.Key = portGroupKey;
		this.HostID = hostID;
		this.Init();
	}

	private Init(): void {
		this.Name = '';
		this.VirtualSwitchKey = '';
		this.VirtualSwitch = '';
		this.NetworkName = '';
		this.VlanId = -1;

		if (this.Ports == null)
			this.Ports = new VirtualPortTable();
		else
			this.Ports.Clear();
	}

	public Write(obj: any, context: any): void {
		obj.virtual_port_group_entry = {};
		obj = obj.virtual_port_group_entry;

		obj.key = this.Key;

		if (this.HasName)
			obj.name = this.Name;

		if (this.HasHostID)
			obj.host_id = this.HostID;

		if (this.HasVirtualSwitchKey)
			obj.virtual_switch_key = this.VirtualSwitchKey;

		if (this.HasVirtualSwitch)
			obj.virtual_switch = this.VirtualSwitch;

		if (this.HasNetworkName)
			obj.network_name = this.NetworkName;

		if (this.HasVlanId)
			obj.vlan_id = ValueEncoder.EncodeInteger(this.VlanId);

		this.Ports.Write(obj, context);
	}

	public Read(obj: any, context: any): void {
		if (!obj.virtual_port_group_entry)
			throw new Error();
		obj = obj.virtual_port_group_entry;

		if (obj.key !== undefined)
			this.Key = obj.key;

		if (obj.name !== undefined)
			this.Name = obj.name;

		if (obj.host_id !== undefined)
			this.HostID = obj.host_id;

		if (obj.virtual_switch_key !== undefined)
			this.VirtualSwitchKey = obj.virtual_switch_key;

		if (obj.virtual_switch !== undefined)
			this.VirtualSwitch = obj.virtual_switch;

		if (obj.network_name !== undefined)
			this.NetworkName = obj.network_name;

		if (obj.vlan_id !== undefined)
			this.VlanId = ValueEncoder.DecodeInteger(obj.vlan_id);

		if (obj.virtual_port_table !== undefined)
			this.Ports.Read(obj, context);
	}
}

export class VirtualPortGroupTable implements ObjectSerializer {

	private _groupTable: Dictionary<string, VirtualPortGroup>;

	public VirtualHost: VirtualHostInfo;
	public get HasVirtualHost(): boolean {
		return this.VirtualHost && this.VirtualHost.HasHostID;
	}

	constructor(parentHost: VirtualHostInfo ) {
		this._groupTable = new Dictionary<string, VirtualPortGroup>();
		this.VirtualHost = parentHost;
	}

	public Add(portGroupKey: string): VirtualPortGroup {
		var vPortGroup: VirtualPortGroup = null;

		if (this._groupTable.ContainsKey(portGroupKey)) {
			vPortGroup = this._groupTable.Item(portGroupKey);
		}
		else {
			vPortGroup = new VirtualPortGroup(portGroupKey, '');
			this._groupTable.Add(portGroupKey, vPortGroup);
		}

		if (this.HasVirtualHost)
			vPortGroup.HostID = this.VirtualHost.HostID;

		return vPortGroup;
	}

	public AddPortGroup(vPortGroup: VirtualPortGroup): void {
		if (vPortGroup == null)
			return;
		if (!vPortGroup.HasKey)
			return;

		if (this._groupTable.ContainsKey(vPortGroup.Key))
			this._groupTable.Remove(vPortGroup.Key);

		this._groupTable.Add(vPortGroup.Key, vPortGroup);

		if (this.HasVirtualHost)
			vPortGroup.HostID = this.VirtualHost.HostID;
	}

	public Clear(): void {
		this._groupTable.Clear();
	}

	public get Count(): number {
		return this._groupTable.Count;
	}

	public Remove(portGroupKey: string):boolean	{
		return this._groupTable.Remove(portGroupKey);
	}

	public Write(obj: any, context: any): void {
		obj.virtual_port_group_table = {};
		obj = obj.virtual_port_group_table;

		obj.entries = [];
		ObjSerUtils.WriteObjSerCollection(obj.entries, this._groupTable.Values(), context);
	}

	public Read(obj: any, context: any): void {
		if (!obj.virtual_port_group_table)
			throw new Error();
		obj = obj.virtual_port_group_table;

		this.Clear();

		if (obj.entries !== undefined) {
			obj.entries.forEach((value: any) => {
				let entry = new VirtualPortGroup();
				entry.Read(value, context);
				this.AddPortGroup(entry);
			}, this);
		}
	}
}
